import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { apiUrl } from '@/config/api';
import { useAuth } from '../context/AuthContext.jsx';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import AuthLanguageSwitcher from '@/components/AuthLanguageSwitcher.jsx';
import CodeInput from '@/components/CodeInput.jsx';
import {
  DEFAULT_LANGUAGE,
  buildLocalizedUrl,
  detectLanguageFromPath,
  getStoredLanguage,
  persistAuthLanguage,
  persistLanguage,
  resolveLanguagePriority,
} from '@/utils/languageRouting.js';

const PHONE_COUNTRY_STORAGE_KEY = 'register-phone-country';

const PHONE_COUNTRIES = {
  ua: {
    code: '38',
    iso: 'UA',
    maxDigits: 10,
    groups: [3, 3, 2, 2],
    placeholder: '(___) ___ __ __',
  },
  us: {
    code: '1',
    iso: 'US',
    maxDigits: 10,
    groups: [3, 3, 4],
    placeholder: '(___) ___ ____',
  },
  pl: {
    code: '48',
    iso: 'PL',
    maxDigits: 9,
    groups: [3, 3, 3],
    placeholder: '___ ___ ___',
  },
  de: {
    code: '49',
    iso: 'DE',
    maxDigits: 11,
    groups: [3, 4, 4],
    placeholder: '___ ____ ____',
  },
  gb: {
    code: '44',
    iso: 'GB',
    maxDigits: 10,
    groups: [4, 6],
    placeholder: '____ ______',
  },
  fr: {
    code: '33',
    iso: 'FR',
    maxDigits: 9,
    groups: [1, 2, 2, 2, 2],
    placeholder: '_ __ __ __ __',
  },
  es: {
    code: '34',
    iso: 'ES',
    maxDigits: 9,
    groups: [3, 3, 3],
    placeholder: '___ ___ ___',
  },
  it: {
    code: '39',
    iso: 'IT',
    maxDigits: 10,
    groups: [3, 3, 4],
    placeholder: '___ ___ ____',
  },
  ca: {
    code: '1',
    iso: 'CA',
    maxDigits: 10,
    groups: [3, 3, 4],
    placeholder: '(___) ___ ____',
  },
  tr: {
    code: '90',
    iso: 'TR',
    maxDigits: 10,
    groups: [3, 3, 2, 2],
    placeholder: '___ ___ __ __',
  },
};

function formatByGroupsWithCursorMap(digits, groups) {
  if (!digits) {
    return { value: '', digitPositions: [] };
  }

  const hasParentheses = groups.length > 0 && groups[0] <= 3 && groups.length >= 3;
  const digitPositions = [];
  let value = '';
  let sourceIndex = 0;

  const appendDigitChunk = (chunk) => {
    for (const digit of chunk) {
      digitPositions.push(value.length);
      value += digit;
    }
  };

  if (hasParentheses) {
    const firstChunk = digits.slice(0, groups[0]);
    if (firstChunk) {
      value += '(';
      appendDigitChunk(firstChunk);
      if (firstChunk.length === groups[0]) {
        value += ')';
      }
      sourceIndex += firstChunk.length;
    }

    for (let groupIndex = 1; groupIndex < groups.length; groupIndex += 1) {
      const groupSize = groups[groupIndex];
      const chunk = digits.slice(sourceIndex, sourceIndex + groupSize);
      if (!chunk) break;
      if (value) {
        value += ' ';
      }
      appendDigitChunk(chunk);
      sourceIndex += chunk.length;
    }

    if (sourceIndex < digits.length) {
      if (value) {
        value += ' ';
      }
      appendDigitChunk(digits.slice(sourceIndex));
    }

    return { value, digitPositions };
  }

  for (const groupSize of groups) {
    const chunk = digits.slice(sourceIndex, sourceIndex + groupSize);
    if (!chunk) break;
    if (value) {
      value += ' ';
    }
    appendDigitChunk(chunk);
    sourceIndex += chunk.length;
  }

  if (sourceIndex < digits.length) {
    if (value) {
      value += ' ';
    }
    appendDigitChunk(digits.slice(sourceIndex));
  }

  return { value, digitPositions };
}

function getDigitIndexFromCursor(value, cursor) {
  return value.slice(0, Math.max(0, cursor)).replace(/\D/g, '').length;
}

function getCursorFromDigitIndex(digitIndex, formattedValue, digitPositions) {
  if (digitIndex <= 0) return 0;
  if (digitIndex > digitPositions.length) return formattedValue.length;
  return digitPositions[digitIndex - 1] + 1;
}

function getCountryFlagUrl(iso) {
  return `https://flagcdn.com/24x18/${iso.toLowerCase()}.png`;
}

function toE164Phone(country, digits) {
  const definition = PHONE_COUNTRIES[country] || PHONE_COUNTRIES.ua;
  const clippedDigits = digits.slice(0, definition.maxDigits);

  if (!clippedDigits) {
    return '';
  }

  return `+${definition.code}${clippedDigits}`;
}

function isTechnicalErrorKey(message) {
  return /^[A-Z][A-Z0-9_]*$/.test(message || '');
}

const RegisterPage = () => {
  const { t, i18n } = useTranslation('dashboard');
  const { register, syncProfileLanguage } = useAuth();
  const { getLocalizedPath, language } = useLanguageNavigation();
  const location = useLocation();
  const routerNavigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', referralCode: '' });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [info, setInfo] = useState('');
  const [pending, setPending] = useState(false);
  const [step, setStep] = useState('form');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [hasAcceptedOffer, setHasAcceptedOffer] = useState(true);
  const [phoneCountry, setPhoneCountry] = useState('us');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [phoneCursorDigitIndex, setPhoneCursorDigitIndex] = useState(null);
  const phoneInputRef = useRef(null);
  const [codeDigits, setCodeDigits] = useState(() => Array(6).fill(''));
  const [expiresAt, setExpiresAt] = useState(null);
  const [cooldownEndsAt, setCooldownEndsAt] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const [searchParams] = useSearchParams();
  const storedLanguage = useMemo(() => getStoredLanguage(), []);
  const resolveAuthLanguage = useCallback(
    () =>
      resolveLanguagePriority(
        detectLanguageFromPath(location.pathname),
        language,
        storedLanguage,
        i18n.language,
        DEFAULT_LANGUAGE,
      ),
    [i18n.language, language, location.pathname, storedLanguage],
  );
  const loginLink = useMemo(() => {
    const queryString = searchParams.toString();
    return getLocalizedPath('/login', queryString ? { search: `?${queryString}` } : undefined);
  }, [getLocalizedPath, searchParams]);

  const phoneConfig = PHONE_COUNTRIES[phoneCountry] || PHONE_COUNTRIES.ua;
  const phoneDisplayMeta = useMemo(
    () => formatByGroupsWithCursorMap(phoneDigits, phoneConfig.groups),
    [phoneConfig.groups, phoneDigits],
  );
  const displayedPhone = phoneDisplayMeta.value;

  useLayoutEffect(() => {
    if (phoneCursorDigitIndex === null || !phoneInputRef.current) {
      return;
    }
    const nextCursor = getCursorFromDigitIndex(phoneCursorDigitIndex, displayedPhone, phoneDisplayMeta.digitPositions);
    phoneInputRef.current.setSelectionRange(nextCursor, nextCursor);
    setPhoneCursorDigitIndex(null);
  }, [displayedPhone, phoneCursorDigitIndex, phoneDisplayMeta.digitPositions]);

  const getSafeReturnUrl = useCallback((selectedLanguage) => {
    const rawReturnUrl = searchParams.get('returnUrl');
    if (!rawReturnUrl) return null;

    try {
      const url = new URL(rawReturnUrl, window.location.origin);
      if (url.origin !== window.location.origin) {
        return null;
      }
      const langInReturn = detectLanguageFromPath(url.pathname);
      const pathWithQuery = `${url.pathname}${url.search}${url.hash}`;

    if (langInReturn) {
      return pathWithQuery;
    }

    return buildLocalizedUrl(url.pathname, selectedLanguage, url.search, url.hash);
  } catch (err) {
    console.warn('Некорректный returnUrl', err);
    return null;
  }
  }, [searchParams]);

  useEffect(() => {
    const timer = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const referralFromQuery = searchParams.get('ref');
    if (referralFromQuery) {
      setForm((prev) => ({ ...prev, referralCode: referralFromQuery }));
    }
  }, [searchParams]);

  useEffect(() => {
    const savedCountry = window.localStorage.getItem(PHONE_COUNTRY_STORAGE_KEY);
    if (savedCountry && PHONE_COUNTRIES[savedCountry]) {
      setPhoneCountry(savedCountry);
      return;
    }
    if (resolveAuthLanguage() === 'ua') {
      setPhoneCountry('ua');
    }
  }, [resolveAuthLanguage]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, phone: toE164Phone(phoneCountry, phoneDigits) }));
  }, [phoneCountry, phoneDigits]);

  const cooldownSecondsLeft = useMemo(() => {
    if (!cooldownEndsAt) return 0;
    return Math.max(0, Math.ceil((new Date(cooldownEndsAt).getTime() - nowTs) / 1000));
  }, [cooldownEndsAt, nowTs]);

  const expirationMinutesLeft = useMemo(() => {
    if (!expiresAt) return null;
    const diff = Math.max(0, new Date(expiresAt).getTime() - nowTs);
    return Math.ceil(diff / 60000);
  }, [expiresAt, nowTs]);

  const getRegisterErrorMessage = useCallback((errorCode) => {
    switch (errorCode) {
      case 'EMAIL_ALREADY_EXISTS':
        return {
          formError: '',
          fieldErrors: { email: t('auth.register.errors.emailAlreadyExists') },
        };
      case 'PHONE_ALREADY_EXISTS':
        return {
          formError: '',
          fieldErrors: { phone: t('auth.register.errors.phoneAlreadyExists') },
        };
      case 'REGISTRATION_SEND_LIMIT_REACHED':
        return {
          formError: t('auth.register.errors.registerCodeDailyLimitReached'),
          fieldErrors: {},
        };
      case 'RATE_LIMIT_EXCEEDED':
        return {
          formError: t('auth.register.errors.rateLimitExceeded'),
          fieldErrors: {},
        };
      default:
        return null;
    }
  }, [t]);

  const parseError = async (response) => {
    try {
      const data = await response.json();
      return {
        code: typeof data?.code === 'string' ? data.code : '',
        message: typeof data?.error === 'string' ? data.error : '',
      };
    } catch (err) {
      return { code: '', message: '' };
    }
  };

  const resolveRegisterApiError = useCallback(async (response) => {
    const apiError = await parseError(response);
    const mappedError = getRegisterErrorMessage(apiError.code || apiError.message);
    if (mappedError) {
      return mappedError;
    }

    return {
      formError: apiError.message && !isTechnicalErrorKey(apiError.message)
        ? apiError.message
        : t('auth.register.errors.requestFailed'),
      fieldErrors: {},
    };
  }, [getRegisterErrorMessage, t]);

  const applyRegisterApiError = useCallback((resolvedError) => {
    if (!resolvedError) {
      setError(t('auth.register.errors.requestFailed'));
      return;
    }

    if (resolvedError.fieldErrors && Object.keys(resolvedError.fieldErrors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...resolvedError.fieldErrors }));
    }

    setError(resolvedError.formError ?? t('auth.register.errors.requestFailed'));
  }, [t]);

  const formatNetworkError = (networkError) => {
    if (networkError?.name === 'TypeError' || networkError?.message === 'Failed to fetch') {
      return t('auth.register.errors.network');
    }
    const mappedError = getRegisterErrorMessage(networkError?.message);
    if (mappedError) {
      if (mappedError.fieldErrors && Object.keys(mappedError.fieldErrors).length > 0) {
        setFieldErrors((prev) => ({ ...prev, ...mappedError.fieldErrors }));
      }
      return mappedError.formError || '';
    }
    return networkError?.message && !isTechnicalErrorKey(networkError.message)
      ? networkError.message
      : t('auth.register.errors.generic');
  };

  const handleSendCode = async () => {
    setPending(true);
    setError('');
    setInfo('');
    try {
      const response = await fetch(apiUrl('/api/auth/email/init'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          name: form.name,
          phone: form.phone,
          emailLanguage: resolveAuthLanguage(),
          operation: 'register',
        }),
      });
      if (!response.ok) {
        applyRegisterApiError(await resolveRegisterApiError(response));
        return;
      }
      const data = await response.json();
      setStep('code');
      setCodeDigits(Array(6).fill(''));
      setExpiresAt(data?.expiresAt || null);
      setCooldownEndsAt(data?.cooldownEndsAt || null);
      setInfo(t('auth.register.messages.codeSent'));
    } catch (err) {
      setError(formatNetworkError(err));
    } finally {
      setPending(false);
    }
  };

  const handleResendCode = async () => {
    if (!form.email) {
      setError('Сначала укажите email');
      return;
    }
    if (cooldownSecondsLeft > 0) {
      return;
    }
    setPending(true);
    setError('');
    setInfo('');
    try {
      const response = await fetch(apiUrl('/api/auth/email/resend'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          name: form.name,
          phone: form.phone,
          emailLanguage: resolveAuthLanguage(),
          operation: 'register',
        }),
      });
      if (!response.ok) {
        applyRegisterApiError(await resolveRegisterApiError(response));
        return;
      }
      const data = await response.json();
      setCooldownEndsAt(data?.cooldownEndsAt || null);
      setExpiresAt(data?.expiresAt || null);
      setCodeDigits(Array(6).fill(''));
      setInfo(t('auth.register.messages.codeResent'));
    } catch (err) {
      setError(formatNetworkError(err));
    } finally {
      setPending(false);
    }
  };

  const handleVerifyAndRegister = async () => {
    const code = codeDigits.join('');
    if (code.length !== 6) {
      setError(t('auth.register.errors.codeRequired'));
      return;
    }

    setPending(true);
    setError('');
    try {
      const verifyResponse = await fetch(apiUrl('/api/auth/email/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, code, operation: 'register' }),
      });
      if (!verifyResponse.ok) {
        applyRegisterApiError(await resolveRegisterApiError(verifyResponse));
        return;
      }

      const targetLanguage = resolveAuthLanguage();
      const authData = await register(form);
      persistLanguage(targetLanguage);
      persistAuthLanguage(targetLanguage);
      if (i18n.language !== targetLanguage) {
        i18n.changeLanguage(targetLanguage);
      }
      await syncProfileLanguage(targetLanguage, authData?.token);
      const targetAfterRegister =
        getSafeReturnUrl(targetLanguage) ||
        getLocalizedPath('/dashboard/create-game', { language: targetLanguage });
      routerNavigate(targetAfterRegister, { replace: true });
    } catch (err) {
      setError(formatNetworkError(err));
    } finally {
      setPending(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) {
        return prev;
      }
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handlePhoneCountryChange = (event) => {
    const nextCountry = event.target.value;
    if (!PHONE_COUNTRIES[nextCountry]) {
      return;
    }
    setPhoneCountry(nextCountry);
    setPhoneCursorDigitIndex(0);
    window.localStorage.setItem(PHONE_COUNTRY_STORAGE_KEY, nextCountry);
    setPhoneDigits((prev) => prev.slice(0, PHONE_COUNTRIES[nextCountry].maxDigits));
    setFieldErrors((prev) => {
      if (!prev.phone) return prev;
      const next = { ...prev };
      delete next.phone;
      return next;
    });
  };

  const handlePhoneChange = (event) => {
    const cursorPosition = event.target.selectionStart ?? event.target.value.length;
    const cleanDigits = event.target.value.replace(/\D/g, '').slice(0, PHONE_COUNTRIES[phoneCountry].maxDigits);
    const nextDigitIndex = Math.min(getDigitIndexFromCursor(event.target.value, cursorPosition), cleanDigits.length);
    setPhoneDigits(cleanDigits);
    setPhoneCursorDigitIndex(nextDigitIndex);
    setFieldErrors((prev) => {
      if (!prev.phone) return prev;
      const next = { ...prev };
      delete next.phone;
      return next;
    });
  };

  const handlePhoneKeyDown = (event) => {
    if ((event.key !== 'Backspace' && event.key !== 'Delete') || !phoneInputRef.current) {
      return;
    }

    const selectionStart = phoneInputRef.current.selectionStart ?? 0;
    const selectionEnd = phoneInputRef.current.selectionEnd ?? selectionStart;

    if (selectionStart !== selectionEnd) {
      return;
    }

    const digitIndexBeforeCursor = getDigitIndexFromCursor(displayedPhone, selectionStart);
    let removeIndex = -1;
    let nextDigitIndex = digitIndexBeforeCursor;

    if (event.key === 'Backspace') {
      const previousChar = displayedPhone[selectionStart - 1] || '';
      if (!previousChar || previousChar.match(/\d/)) {
        return;
      }
      removeIndex = digitIndexBeforeCursor - 1;
      nextDigitIndex = Math.max(0, digitIndexBeforeCursor - 1);
    } else {
      const currentChar = displayedPhone[selectionStart] || '';
      if (!currentChar || currentChar.match(/\d/)) {
        return;
      }
      removeIndex = digitIndexBeforeCursor;
      nextDigitIndex = digitIndexBeforeCursor;
    }

    if (removeIndex < 0 || removeIndex >= phoneDigits.length) {
      return;
    }

    event.preventDefault();
    setPhoneDigits((prev) => `${prev.slice(0, removeIndex)}${prev.slice(removeIndex + 1)}`);
    setPhoneCursorDigitIndex(nextDigitIndex);
    setFieldErrors((prev) => {
      if (!prev.phone) return prev;
      const next = { ...prev };
      delete next.phone;
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (step === 'form' && !hasAcceptedOffer) {
      return;
    }
    if (step === 'form') {
      const nextFieldErrors = {};
      ['name', 'email', 'phone', 'password'].forEach((fieldName) => {
        if (fieldName === 'phone') {
          if (!phoneDigits) {
            nextFieldErrors.phone = t('validation.required');
          }
          return;
        }
        if (!form[fieldName]?.trim()) {
          nextFieldErrors[fieldName] = t('validation.required');
        }
      });
      setFieldErrors(nextFieldErrors);
      if (Object.keys(nextFieldErrors).length > 0) {
        return;
      }
    }

    if (step === 'code') {
      await handleVerifyAndRegister();
    } else {
      await handleSendCode();
    }
  };

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={handleSubmit} noValidate>
        <AuthLanguageSwitcher />
        <h1>{t('auth.register.title')}</h1>
        {step === 'form' && (
          <p className="auth-subtitle">{t('auth.register.subtitle')}</p>
        )}

        {step === 'form' && (
          <>
            <label>
              {t('auth.register.fields.name.label')}
              <input
                name="name"
                type="text"
                placeholder={t('auth.register.namePlaceholder')}
                value={form.name}
                onChange={handleChange}
                required
              />
              {fieldErrors.name && <span className="auth-field-error">{fieldErrors.name}</span>}
            </label>
            <label>
              {t('auth.register.fields.email.label')}
              <input
                name="email"
                type="email"
                placeholder={t('auth.register.emailPlaceholder')}
                value={form.email}
                onChange={handleChange}
                required
              />
              {fieldErrors.email && <span className="auth-field-error">{fieldErrors.email}</span>}
            </label>
            <label>
              {t('auth.register.fields.phone.label')}
              <div className="auth-phone-input" role="group" aria-label={t('auth.register.fields.phone.label')}>
                <div className="auth-phone-country-wrap">
                  <img className="auth-phone-flag" src={getCountryFlagUrl(phoneConfig.iso)} alt={t('auth.register.fields.phone.countryLabel')} width="24" height="18" loading="lazy" decoding="async" />
                  <select
                    value={phoneCountry}
                    onChange={handlePhoneCountryChange}
                    className="auth-phone-country-select"
                    aria-label={t('auth.register.fields.phone.countryLabel')}
                  >
                    {Object.entries(PHONE_COUNTRIES).map(([countryCode, config]) => (
                      <option key={countryCode} value={countryCode}>{`${config.iso} +${config.code}`}</option>
                    ))}
                  </select>
                </div>
                <div className="auth-phone-number-wrap">
                  <span className="auth-phone-prefix">+{phoneConfig.code}</span>
                  <input
                    ref={phoneInputRef}
                    name="registration-phone-manual"
                    type="tel"
                    inputMode="numeric"
                    placeholder={phoneConfig.placeholder}
                    value={displayedPhone}
                    onChange={handlePhoneChange}
                    onKeyDown={handlePhoneKeyDown}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    required
                  />
                </div>
              </div>
              <input type="text" tabIndex={-1} aria-hidden="true" className="auth-phone-decoy" autoComplete="new-password" />
              {fieldErrors.phone && <span className="auth-field-error">{fieldErrors.phone}</span>}
            </label>
            <label>
              {t('auth.register.fields.password.label')}
              <div className="auth-input-with-action">
                <input
                  name="password"
                  type={isPasswordVisible ? 'text' : 'password'}
                  placeholder={t('auth.register.fields.password.placeholder')}
                  value={form.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  aria-label={isPasswordVisible ? t('auth.password.hide') : t('auth.password.show')}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setIsPasswordVisible((prev) => !prev)}
                >
                  {isPasswordVisible ? '🙈' : '👁️'}
                </button>
              </div>
              {fieldErrors.password && <span className="auth-field-error">{fieldErrors.password}</span>}
            </label>
            <label className="auth-checkbox-label">
              <input
                type="checkbox"
                checked={hasAcceptedOffer}
                onChange={(event) => setHasAcceptedOffer(event.target.checked)}
              />
              <span className="auth-checkbox-text">
                <Trans
                  i18nKey="auth.register.offerAgreement"
                  ns="dashboard"
                  components={{
                    offerLink: <a href="https://leelaspace.com/dogovir-publichnoyi-oferti" target="_blank" rel="noreferrer" />,
                    returnLink: <a href="https://leelaspace.com/umovi-povernennya-ta-obminu" target="_blank" rel="noreferrer" />,
                  }}
                />
              </span>
            </label>
          </>
        )}

        {step === 'code' && (
          <div className="code-section">
            <div className="code-step">
              <div className="code-head">
                <h2 className="code-title">{t('auth.register.code.title')}</h2>
                <p className="code-subtitle">
                  <Trans
                    i18nKey="auth.register.code.subtitle"
                    ns="dashboard"
                    values={{ email: form.email || t('auth.register.code.fallbackEmail') }}
                    components={{ strong: <strong /> }}
                  />
                  {expirationMinutesLeft !== null && expirationMinutesLeft > 0 && (
                    <> {t('auth.register.code.expiration', { minutes: expirationMinutesLeft })}</>
                  )}
                </p>
              </div>
              <CodeInput
                digits={codeDigits}
                onChange={setCodeDigits}
                autoFocus={step === 'code'}
                getAriaLabel={(index) => t('auth.register.code.ariaLabel', { index: index + 1 })}
              />
              <div className="code-actions">
                <button type="button" className="ghost" onClick={handleResendCode} disabled={pending || cooldownSecondsLeft > 0}>
                  {cooldownSecondsLeft > 0
                    ? t('auth.register.code.resendIn', { seconds: cooldownSecondsLeft })
                    : t('auth.register.code.resend')}
                </button>
              </div>
            </div>
          </div>
        )}
        {info && <p className="auth-info">{info}</p>}
        {error && <p className="auth-error">{error}</p>}
        <button type="submit" disabled={pending || (step === 'form' && !hasAcceptedOffer)} aria-busy={pending}>
          {pending
            ? t('auth.register.actions.pending')
            : step === 'code'
              ? t('auth.register.actions.confirm')
              : t('auth.register.actions.submit')}
        </button>
        {step === 'code' && (
          <button type="button" onClick={() => setStep('form')} className="link-btn">
            {t('auth.register.actions.changeData')}
          </button>
        )}
        <p className="auth-helper">
          {t('auth.register.helper')} <Link to={loginLink}>{t('auth.register.login')}</Link>
        </p>
      </form>
    </div>
  );
};

export default RegisterPage;
